// keyrd_console/src/components/PilotAlerts.js
import React, { useEffect, useMemo, useState } from "react";
import { getPilotAlerts } from "../api/pilotAdmin";
import PilotGovernanceBanner from "./PilotGovernanceBanner";
import PilotStatusPill from "./PilotStatusPill";
import PilotDataSourceBadge from "./PilotDataSourceBadge";

const pageStyle = {
  padding: 20,
  display: "flex",
  flexDirection: "column",
  gap: 16,
};

const titleRowStyle = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  flexWrap: "wrap",
  gap: 12,
};

const titleStyle = {
  fontSize: 24,
  fontWeight: 700,
  color: "#0f172a",
  margin: 0,
};

const subtitleStyle = {
  color: "#475569",
  fontSize: 14,
  marginTop: 4,
};

const controlRowStyle = {
  display: "flex",
  gap: 10,
  flexWrap: "wrap",
  alignItems: "center",
};

const inputStyle = {
  padding: "10px 12px",
  borderRadius: 10,
  border: "1px solid #cbd5e1",
  minWidth: 260,
};

const selectStyle = {
  padding: "10px 12px",
  borderRadius: 10,
  border: "1px solid #cbd5e1",
  background: "#ffffff",
};

const listStyle = {
  display: "flex",
  flexDirection: "column",
  gap: 12,
};

const itemStyle = {
  background: "#ffffff",
  border: "1px solid #e2e8f0",
  borderRadius: 12,
  padding: 16,
  display: "flex",
  flexDirection: "column",
  gap: 10,
  cursor: "pointer",
};

const itemTopStyle = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "flex-start",
  gap: 12,
  flexWrap: "wrap",
};

const itemTitleStyle = {
  fontSize: 16,
  fontWeight: 700,
  color: "#0f172a",
};

const metaTextStyle = {
  fontSize: 13,
  color: "#64748b",
};

const rowWrapStyle = {
  display: "flex",
  flexWrap: "wrap",
  gap: 8,
};

const loadingStyle = {
  padding: 18,
  borderRadius: 10,
  background: "#f8fafc",
  border: "1px solid #e2e8f0",
  color: "#334155",
};

const errorStyle = {
  padding: 14,
  borderRadius: 10,
  background: "#fef2f2",
  color: "#991b1b",
  border: "1px solid #fecaca",
};

const emptyStyle = {
  padding: 18,
  borderRadius: 10,
  background: "#f8fafc",
  border: "1px solid #e2e8f0",
  color: "#475569",
};

function normalizeRows(payload) {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.alerts)) return payload.alerts;
  if (Array.isArray(payload?.rows)) return payload.rows;
  return [];
}

function matchesQuery(row, query) {
  if (!query) return true;
  const haystack = [
    row.participant_uuid,
    row.participant_label,
    row.alert_type,
    row.alert_family,
    row.description,
    row.severity,
  ]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();

  return haystack.includes(query.toLowerCase());
}

function filterRows(rows, query, severity) {
  return rows.filter((row) => {
    const queryOk = matchesQuery(row, query);
    const severityOk =
      severity === "all" ||
      String(row.severity || "").toLowerCase() === severity;
    return queryOk && severityOk;
  });
}

function PilotAlerts({ auth, onSelectParticipant }) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [payload, setPayload] = useState(null);
  const [query, setQuery] = useState("");
  const [severity, setSeverity] = useState("all");

  useEffect(() => {
    let cancelled = false;

    async function load() {
      setLoading(true);
      setError("");

      try {
        const res = await getPilotAlerts(auth);
        if (!cancelled) {
          setPayload(res?.data || res || {});
        }
      } catch (err) {
        if (!cancelled) {
          setError(err?.message || "Failed to load pilot alerts.");
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    }

    load();
    return () => {
      cancelled = true;
    };
  }, [auth]);

  const data = payload || {};
  const rows = normalizeRows(data);
  const filteredRows = useMemo(
    () => filterRows(rows, query, severity),
    [rows, query, severity]
  );

  if (loading) {
    return (
      <div style={pageStyle}>
        <div style={loadingStyle}>Loading pilot alerts…</div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={pageStyle}>
        <div style={errorStyle}>{error}</div>
      </div>
    );
  }

  return (
    <div style={pageStyle}>
      <div style={titleRowStyle}>
        <div>
          <h1 style={titleStyle}>Pilot Alerts</h1>
          <div style={subtitleStyle}>
            Operational exceptions across signals, burden, suppression, cooldown, pause, and governance.
          </div>
        </div>
        <PilotDataSourceBadge source={data.data_source} stub={Boolean(data.stub)} />
      </div>

      <PilotGovernanceBanner auth={auth} system={data.system || data} />

      <div style={controlRowStyle}>
        <input
          style={inputStyle}
          type="text"
          value={query}
          placeholder="Search alert, participant, severity…"
          onChange={(e) => setQuery(e.target.value)}
        />
        <select
          style={selectStyle}
          value={severity}
          onChange={(e) => setSeverity(e.target.value)}
        >
          <option value="all">All severities</option>
          <option value="info">Info</option>
          <option value="warning">Warning</option>
          <option value="high">High</option>
          <option value="critical">Critical</option>
        </select>
      </div>

      {!filteredRows.length ? (
        <div style={emptyStyle}>No alerts matched the current filters.</div>
      ) : (
        <div style={listStyle}>
          {filteredRows.map((row, idx) => (
            <div
              key={`${row.alert_id || row.participant_uuid || "alert"}-${idx}`}
              style={itemStyle}
              onClick={() =>
                typeof onSelectParticipant === "function" &&
                row.participant_uuid &&
                onSelectParticipant(row.participant_uuid)
              }
            >
              <div style={itemTopStyle}>
                <div>
                  <div style={itemTitleStyle}>
                    {row.alert_type || "Alert"}
                  </div>
                  <div style={metaTextStyle}>
                    {row.participant_label || "Unknown Participant"} ·{" "}
                    {row.participant_uuid || "No UUID"}
                  </div>
                </div>
                <PilotStatusPill
                  status={row.severity || "unknown"}
                  label={row.severity || "unknown"}
                />
              </div>

              <div style={rowWrapStyle}>
                <PilotStatusPill
                  status={row.alert_family || "unknown"}
                  label={`Family: ${row.alert_family || "unknown"}`}
                />
                <PilotStatusPill
                  status={row.acknowledged ? "true" : "false"}
                  label={row.acknowledged ? "Acknowledged" : "Unacknowledged"}
                />
              </div>

              <div style={metaTextStyle}>
                {row.description || "No alert description provided."}
              </div>
              <div style={metaTextStyle}>
                Created: {row.timestamp_utc || "unknown"}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default PilotAlerts;